import { CrudService } from '@/services/crudService';
import { config } from '@/services/configService';
import { normalizeBaseUrl } from '@/helpers/tenantUrlHelper';

type TenantMainSettings = {
  priceWithTaxes: boolean | null;
};

const settingsCache = new Map<string, Promise<TenantMainSettings | null> | TenantMainSettings | null>();

function normalizeSettingsKey(tenantUrl?: string | null): string {
  const normalized = normalizeBaseUrl(tenantUrl);
  if (normalized) {
    return normalized;
  }
  return normalizeBaseUrl(config.host) || config.host;
}

function parsePriceWithTaxes(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  return null;
}

function extractPriceWithTaxes(payload: unknown): boolean | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const root = payload as Record<string, unknown>;
  const dataCandidate = root.data ?? root.Data ?? null;
  const data = dataCandidate && typeof dataCandidate === 'object'
    ? (dataCandidate as Record<string, unknown>)
    : root;

  const value = data.priceWithTaxes ?? data.PriceWithTaxes ?? root.priceWithTaxes ?? root.PriceWithTaxes ?? null;
  return parsePriceWithTaxes(value);
}

export async function fetchTenantMainSettings(tenantUrl?: string | null): Promise<TenantMainSettings | null> {
  const key = normalizeSettingsKey(tenantUrl);
  if (settingsCache.has(key)) {
    const cached = settingsCache.get(key) ?? null;
    if (cached instanceof Promise) {
      return cached;
    }
    return cached;
  }

  const loader = (async () => {
    try {
      const baseUrl = key;
      const crud = new CrudService({
        baseUrl,
        allowAuth: false,
        handleUnauthorized: false,
      });
      const response = await crud.httpGet('/api/czconfigs/main-settings', null, undefined, 'json', {
        headers: {
          Accept: 'application/json',
        },
        baseUrl,
      });
      const priceWithTaxes = extractPriceWithTaxes(response);
      return {
        priceWithTaxes,
      };
    } catch (error) {
      return null;
    }
  })();

  settingsCache.set(key, loader);
  const resolved = await loader;
  settingsCache.set(key, resolved);
  return resolved;
}

export async function resolvePriceWithTaxes(tenantUrl?: string | null): Promise<boolean | null> {
  const settings = await fetchTenantMainSettings(tenantUrl);
  if (!settings) {
    return null;
  }
  if (typeof settings.priceWithTaxes === 'boolean') {
    return settings.priceWithTaxes;
  }
  return null;
}
